import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { getFieldData } from "./FOFOReport";
import { AuthState } from "context/AuthContext";
import { roleAccess } from "../EboPaymentWorking";

const toNumber = (value) => {
	const parsed = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
	return Number.isFinite(parsed) ? parsed : 0;
};

const getStoreName = (row) =>
	row?.store_display_name || row?.store_name || row?.storeName || row?.store || row?.name || row?.STORE_NAME || row?.SHORT_CODE || "";

const getStoreId = (row, index, franchise = "") =>
	row?.store_id ?? row?.storeId ?? row?.id ?? row?._id ?? row?.STORE_ID ?? row?.short_code ?? row?.SHORT_CODE ?? `${franchise}-${getStoreName(row) || index + 1}`;

const getStoreCode = (row) =>
	row?.store_code || row?.storeCode || row?.short_code || row?.SHORT_CODE || row?.STORE_ID || row?.store_id || row?.storeId || row?.id || row?._id || "";

const formatMonthYear = (value) => {
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.startOf("month").format("YYYY-MM-DD") : "";
};

const normalizeFranchiseValue = (value) => {
	if (value === null || value === undefined) return "";
	if (typeof value === "string" || typeof value === "number") return String(value);
	if (typeof value === "object") {
		return String(value?.franchise ?? value?.name ?? value?.key ?? value?.label ?? value?.value ?? "");
	}
	return String(value);
};

const getFranchiseLabel = (option) => {
	const value = option?.name ?? option?.label ?? option?.display_name ?? option?.franchise ?? option?.key ?? option?.value ?? "";
	return normalizeFranchiseValue(value);
};

const getResponseRows = (data) => {
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.data)) return data.data;
	if (Array.isArray(data?.result)) return data.result;
	if (Array.isArray(data?.results)) return data.results;
	if (Array.isArray(data?.rows)) return data.rows;
	if (Array.isArray(data?.stores)) return data.stores;
	if (Array.isArray(data?.storeRows)) return data.storeRows;
	if (data && typeof data === "object") return [data];
	return [];
};

const looksLikeStoreRow = (row) =>
	Boolean(
		getStoreName(row) ||
		row?.store_id ||
		row?.storeId ||
		row?.store ||
		row?.received_through_cash !== undefined ||
		row?.received_through_card !== undefined ||
		row?.cash_amount !== undefined ||
		row?.card_amount !== undefined
	);

const normalizeStoreRows = (data = [], franchise = "", selectedMonthYear = null) =>
	data.map((row, index) => {
		const cash = toNumber(row?.received_through_cash || row?.cash_amount || row?.cash);
		const card = toNumber(row?.received_through_card || row?.card_amount || row?.card);
		const gv = toNumber(row?.gv || row?.gift_card || row?.giftCard || row?.received_through_gift_card);

		return {
			...row,
			id: getStoreId(row, index, franchise),
			franchise,
			month_year: formatMonthYear(selectedMonthYear || row?.month_year || row?.monthYear || row?.month || ""),
			store_code: getStoreCode(row) || getStoreId(row, index, franchise),
			store_id: getStoreId(row, index, franchise),
			admsite_code: row?.admsite_code,
			store_display_name: getStoreName(row),
			received_through_cash: cash,
			received_through_card: card,
			gv,

			total_received: cash + card + gv,
		};
	});

function FOFOCashCardCollection() {
	const [monthYear, setMonthYear] = useState(null);
	const [franchise, setFranchise] = useState("");
	const [franchiseList, setFranchiseList] = useState([]);
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [activeFranchise, setActiveFranchise] = useState("");
	const [activeRecordId, setActiveRecordId] = useState("");
	const [hasLoadedStoreTable, setHasLoadedStoreTable] = useState(false);
	const [isExistingRecord, setIsExistingRecord] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);

	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();

	const roleAccessData = roleAccess(user);
	const canEditRows = !isExistingRecord || isEditMode;

	const columns = useMemo(
		() => [
			{
				field: "month_year",
				headerName: "Month",
				flex: 0.8,
				minWidth: 120,
				renderCell: (params) => {
					const rawValue = params?.row?.month_year ?? params?.value ?? "";
					const parsed = dayjs(rawValue);
					return parsed.isValid() ? parsed.format("MMM YYYY") : rawValue;
				},
			},
			{
				field: "store_name",
				headerName: "Store",
				flex: 1.4,
				minWidth: 220,
			},
			{
				field: "site_code",
				headerName: "Store Code",
				flex: 0.9,
				minWidth: 140,
			},
			{
				field: "admsite_code",
				headerName: "Adm Site Code",
				flex: 0.9,
				minWidth: 140,
			},
			{
				field: "received_through_cash",
				headerName: "Cash",
				type: "number",
				editable: canEditRows,
				flex: 0.7,
				minWidth: 130,
			},
			{
				field: "received_through_card",
				headerName: "Card",
				type: "number",
				editable: canEditRows,
				flex: 0.7,
				minWidth: 130,
			},
			{
				field: "gv",
				headerName: "GV",
				type: "number",
				editable: canEditRows,
				flex: 0.7,
				minWidth: 130,
			},
			{
				field: "total_received",
				headerName: "Total",
				type: "number",
				flex: 0.7,
				minWidth: 130,
			},
		],
		[canEditRows]
	);

	const franchiseOptions = useMemo(
		() =>
			(franchiseList || []).map((option) => ({
				...option,
				value: normalizeFranchiseValue(option?.key ?? option?.franchise ?? option?.name),
				label: getFranchiseLabel(option),
			})),
		[franchiseList]
	);

	const showSnack = (message, severity = "error") => {
		setSnackBarOpen(true);
		setSnackBar({
			...snackBar,
			message,
			severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		});
	};

	const fetchFranchises = async () => {
		try {
			setLoadingOpen(true);
			const res = await pythonVmInstance({
				url: "/ebo-payment-working/master",
				headers: { "Content-Type": "application/json" },
				params: { master_type: "cash_card" },
				method: "GET",
			});
			setFranchiseList(getFieldData(res?.data));
		} catch (err) {
			showSnack("Failed to fetch franchise list");
		} finally {
			setLoadingOpen(false);
		}
	};

	useEffect(() => {
		fetchFranchises();
	}, []);

	const fetchStoresForFranchise = async (selectedFranchise = franchise, selectedMonthYear = monthYear, selectedRecordId = activeRecordId) => {
		const resolvedFranchise = normalizeFranchiseValue(selectedFranchise || franchise || activeFranchise);
		const resolvedMonthYear = selectedMonthYear || monthYear;
		if (!resolvedFranchise || !resolvedMonthYear) {
			showSnack("Select both month and franchise before getting stores");
			return;
		}

		try {
			setLoading(true);
			setLoadingOpen(true);
			setError("");
			setHasLoadedStoreTable(false);
			setIsExistingRecord(false);
			setIsEditMode(false);

			let existingRecord = null;
			let storeRows = [];
			try {
				const existingRes = await pythonVmInstance({
					url: "/ebo-payment-working/cash-card",
					headers: { "Content-Type": "application/json" },
					params: {
						store_type: "fofo",
						franchise: resolvedFranchise,
						month_year: formatMonthYear(resolvedMonthYear),
						...(selectedRecordId ? { id: selectedRecordId } : {}),
					},
					method: "GET",
				});
				const existingRows = getResponseRows(existingRes?.data);
				existingRecord =
					existingRows.find(
						(record) =>
							String(record?.id ?? record?._id ?? "") === String(selectedRecordId || "") ||
							(String(normalizeFranchiseValue(record?.franchise || record?.franchise_name || record?.franchiseName)) === String(resolvedFranchise) &&
								String(formatMonthYear(record?.month_year || record?.monthYear || record?.month || "")) === String(formatMonthYear(resolvedMonthYear)))
					) || existingRows[0] || null;

				const nestedStoreRows = getResponseRows(
					existingRecord?.stores || existingRecord?.cash_card_stores || existingRecord?.franchiseCashCard || existingRecord?.store_rows || existingRecord?.storeRows
				);
				const flatStoreRows = existingRows.filter(looksLikeStoreRow);
				storeRows = nestedStoreRows.length ? nestedStoreRows : flatStoreRows;
			} catch (err) {
				existingRecord = null;
			}

			setActiveFranchise(resolvedFranchise);
			setHasLoadedStoreTable(true);

			if (storeRows.length) {
				const normalizedRows = normalizeStoreRows(storeRows, resolvedFranchise, resolvedMonthYear);
				setRows(normalizedRows);
				setIsExistingRecord(true);
				setIsEditMode(false);
				setError("");
				if (existingRecord?.id || existingRecord?._id) {
					setActiveRecordId(String(existingRecord.id ?? existingRecord._id));
				}
			} else {
				const storesRes = await pythonVmInstance({
					url: "/ebo-payment-working/franchise-stores",
					headers: { "Content-Type": "application/json" },
					params: {
						store_type: "fofo",
						franchise: resolvedFranchise,
					},
					method: "GET",
				});
				const franchiseStores = getResponseRows(storesRes?.data);
				const normalizedRows = normalizeStoreRows(franchiseStores, resolvedFranchise, resolvedMonthYear);
				setRows(normalizedRows);
				setIsExistingRecord(false);
				setIsEditMode(false);
				setActiveRecordId("");
				setError("");
			}
		} catch (err) {
			setRows([]);
			setActiveFranchise("");
			setHasLoadedStoreTable(false);
			setIsExistingRecord(false);
			setIsEditMode(false);
			showSnack(err?.response?.data?.message || "Failed to fetch stores");
		} finally {
			setLoading(false);
			setLoadingOpen(false);
		}
	};

	const handleRowUpdate = (updatedRow) => {
		const cash = toNumber(updatedRow.received_through_cash);
		const card = toNumber(updatedRow.received_through_card);
		const gv = toNumber(updatedRow.gv);
		const nextRow = {
			...updatedRow,
			received_through_cash: cash,
			received_through_card: card,
			gv,
			total_received: cash + card + gv,
		};
		setRows((prevRows) => prevRows.map((row) => (row.id === nextRow.id ? nextRow : row)));
		return nextRow;
	};

	const handleSave = async () => {
		const resolvedFranchise = normalizeFranchiseValue(activeFranchise);
		if (!rows.length || !resolvedFranchise || !monthYear) return;

		try {
			setSaving(true);
			setLoadingOpen(true);
			await pythonVmInstance({
				url: "/ebo-payment-working/cash-card",
				headers: { "Content-Type": "application/json" },
				method: isExistingRecord ? "PUT" : "POST",
				data: {
					store_type: "fofo",
					...(isExistingRecord && activeRecordId ? { id: activeRecordId } : {}),
					franchise: resolvedFranchise,
					month_year: formatMonthYear(monthYear),
					email: user.email,
					stores: rows.map((row) => ({
						...row,
						received_through_cash: toNumber(row.received_through_cash),
						received_through_card: toNumber(row.received_through_card),
						gv: toNumber(row.gv),
						total_received: toNumber(row.total_received),
					})),
				},
			});
			showSnack(isExistingRecord ? "Cash and card amounts updated successfully" : "Cash and card amounts saved successfully", "success");
			if (isExistingRecord) {
				setRows((prevRows) =>
					prevRows.map((row) => ({
						...row,
						received_through_cash: toNumber(row.received_through_cash),
						received_through_card: toNumber(row.received_through_card),
						gv: toNumber(row.gv),
						total_received: toNumber(row.total_received),
					}))
				);
				setIsEditMode(false);
				setError("");
			} else {
				setRows([]);
				setFranchise("");
				setMonthYear(null);
				setActiveFranchise("");
				setActiveRecordId("");
				setHasLoadedStoreTable(false);
				setIsExistingRecord(false);
				setIsEditMode(false);
				setError("");
			}
		} catch (err) {
			showSnack(err?.response?.data?.message || `Failed to ${isExistingRecord ? "update" : "save"} cash and card amounts`);
		} finally {
			setSaving(false);
			setLoadingOpen(false);
		}
	};

	return (
		<Box width="100%" p={1.5}>
			<Stack spacing={1.5}>
				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
					<Stack spacing={1.5}>
						<Typography variant="subtitle1" fontWeight={700}>
							FOFO Cash/Card Collection
						</Typography>

						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }}>
						<DatePicker
									views={["year", "month"]}
									openTo="month"
									label="Month-Year"
									value={monthYear}
									maxDate={dayjs().endOf("month")}
									onChange={(newValue) => {
										setMonthYear(newValue);
										setRows([]);
										setActiveFranchise("");
										setActiveRecordId("");
										setHasLoadedStoreTable(false);
										setIsExistingRecord(false);
										setIsEditMode(false);
									}}
									disabled={loading || saving}
									slotProps={{
										textField: {
											size: "small",
											sx: {
												width: { xs: "100%", md: 160 },
												"& .MuiInputBase-root": {
													height: 38,
													borderRadius: 1.5,
												},
												"& .MuiInputBase-input": {
													py: 0.85,
													fontSize: 13,
												},
											},
										},
									}}
									format="MMM YYYY"
								/>

								<TextField
									select
									size="small"
									label="Select Franchise"
									value={franchise}
									onChange={(event) => {
										setFranchise(normalizeFranchiseValue(event.target.value));
										setRows([]);
										setActiveFranchise("");
										setActiveRecordId("");
										setHasLoadedStoreTable(false);
										setIsExistingRecord(false);
										setIsEditMode(false);
									}}
									disabled={loading || saving}
									sx={{
										minWidth: { xs: "100%", md: 220 },
										"& .MuiInputBase-root": {
											height: 38,
											borderRadius: 1.5,
										},
										"& .MuiInputBase-input": {
											py: 0.85,
											fontSize: 13,
										},
										"& .MuiOutlinedInput-notchedOutline": {
											borderColor: "rgba(0,0,0,0.14)",
										},
									}}>
									{franchiseList.length === 0 ? (
										<MenuItem value="" disabled>
											No franchise available
										</MenuItem>
									) : (
										franchiseOptions.map((option) => (
											<MenuItem key={option.value} value={option.value}>
												{option.label}
											</MenuItem>
										))
									)}
								</TextField>

								<Button
									variant="contained"
									startIcon={<SearchIcon />}
									onClick={() => fetchStoresForFranchise(franchise, monthYear)}
									disabled={loading || saving || !normalizeFranchiseValue(franchise) || !monthYear}
									size="small"
									sx={{ height: 38, px: 2 }}>
									Get Stores
								</Button>
							</Stack>
						</LocalizationProvider>

						{error ? <Alert severity="warning">{error}</Alert> : null}
						{!monthYear || !franchise ? (
							<Alert severity="info">Select both month and franchise, then click Get Stores to load or add cash/card data.</Alert>
						) : null}
					</Stack>
				</Paper>

				{hasLoadedStoreTable ? (
					<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
						<Stack spacing={1.5}>
							<Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
								<Stack spacing={0.25}>
									<Typography variant="subtitle1" fontWeight={700}>
										{isExistingRecord ? "Store Wise Amounts" : "Add Cash/Card Amounts"}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{activeFranchise
											? `${isExistingRecord && !isEditMode ? "Saved record" : canEditRows ? "Editing" : "View"} for ${activeFranchise}`
											: "Select franchise and month"}
									</Typography>
								</Stack>
								{isExistingRecord && !isEditMode ? (
									<Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
										<Button
											variant="contained"
											onClick={() => setIsEditMode(true)}
											disabled={loading || saving || !rows.length || roleAccessData.roles === "VIEWER"}
											size="small"
											sx={{ height: 38, px: 2 }}>
											Edit
										</Button>
									</Box>
								) : null}
							</Stack>

							{!isExistingRecord ? (
								<Alert severity="info" sx={{ py: 0.5, alignItems: "center" }}>
									No saved cash/card record found. We loaded all stores for this franchise so you can enter cash/card values and save a new record.
								</Alert>
							) : null}

							<Box sx={{ height: { xs: "calc(100vh - 420px)", md: "calc(100vh - 340px)" }, minHeight: 320, width: "100%" }}>
								<DataGrid
									rows={rows}
									columns={columns}
									loading={loading}
									processRowUpdate={canEditRows ? handleRowUpdate : undefined}
									onProcessRowUpdateError={canEditRows ? (err) => showSnack(err?.message || "Failed to update row") : undefined}
									disableRowSelectionOnClick
									density="compact"
									pageSizeOptions={[25, 50, 100]}
									initialState={{
										pagination: {
											paginationModel: { pageSize: 25, page: 0 },
										},
									}}
									sx={{
										border: "none",
										fontSize: 13,
										"& .MuiDataGrid-row": {
											minHeight: 36,
											maxHeight: 36,
										},
										"& .MuiDataGrid-cell": {
											py: 0.5,
										},
										"& .MuiDataGrid-columnHeaders": {
											backgroundColor: "#f5f5f5",
											minHeight: 40,
											maxHeight: 40,
										},
									}}
								/>
							</Box>

							<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
								{isExistingRecord && isEditMode ? (
									<Button
										variant="text"
										onClick={() => {
											setIsEditMode(false);
											fetchStoresForFranchise(activeFranchise || franchise, monthYear, activeRecordId);
										}}
										disabled={loading || saving}
										size="small"
										sx={{ height: 38, px: 2 }}>
										Cancel
									</Button>
								) : null}
								{canEditRows ? (
									<Button
										variant="outlined"
										onClick={handleSave}
										disabled={loading || saving || !rows.length || !activeFranchise || !monthYear || roleAccessData.roles === "VIEWER"}
										size="small"
										sx={{ height: 38, px: 2 }}>
										{saving ? "Saving..." : "Save"}
									</Button>
								) : null}
							</Box>
						</Stack>
					</Paper>
				) : null}
			</Stack>
		</Box>
	);
}

export default FOFOCashCardCollection;
